import React, { useState, useEffect, useMemo } from "react";
import Widget from "../../Widget/Widget";
import WidgetShoppingBasket from "../WidgetsSettings/ShoppingBasket/BasketSettingsModal";
import SettingsModal from "../../Modal/SettingsModal";
import IntlMessages from "@util/IntlMessages";
import "../WidgetsSettings/ShoppingBasket/ShoppingBasket.less";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Spin, Message, Input } from "../../BP2Components";
import {
   changeProductQuantity,
   removeCartItem,
   setCart,
   setHasBeenFetched,
} from "../../../appRedux/actions/Cart";
import { useDebouncedCallback } from "use-debounce";
import { getUserWithBoards, postCart } from "../../../services/restAPI/RestApi";
import { getFilteredList, getList } from "../../../services/api/Api";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";
import { mobileStartActionByUrl, isCzg } from "@util/Scripts";
import ActionsDropdown from "../../ActionsDropdownOLD/ActionsDropdown";
import { fixedHTML } from "../../AdvertisementItem/utils";
import KeyInfoItem from "../../AdvertisementItem/subComponents/KeyInfoItem/KeyInfoItem";
import { isActivePublicUser, mobileStartFlutterActionByUrl } from "../../../util/Scripts";

import projectConfig from "@projectConfig";
import { Icon_Delete, Icon_Loading, Icon_Minus, Icon_Plus } from "../../BP2Components/Icons/icons";

const isBudgetError = (errorId) =>
   errorId === "shopping.basket.exceeded" ||
   errorId === "api.shop.cart.error.not_enough_products_in_shop";

export const getCartId = (user, shopId) => {
   if (user?.carts?.length && shopId) {
      const carts = user.carts;
      const findCartByShopId = carts.find((cart) => cart.shopId === shopId);
      const cartId = findCartByShopId?.id;
      return cartId;
   }
};

const ShoppingBasketCardsWidget = ({ value, role }) => {
   const decrease = "decrease";
   const increase = "increase";
   const setQuantity = "setQuantity";
   const deleteProduct = "deleteItem";
   const addProduct = "addItem";
   const intl = useIntl();
   const history = useHistory();
   const { listWidgetSeries } = value;
   const [rows, setRows] = useState([]);
   const [columns, setColumns] = useState([]);
   const [buttons, setButtons] = useState([]);
   const [userPoints, setUserPoints] = useState();
   const cart = useSelector(({ cart }) => cart);

   const user = useSelector(({ user }) => user);
   const { authUser } = useSelector(({ auth }) => auth);

   const [values, setValues] = useState({});
   const [warnings, setWarnings] = useState({});
   const [disableCheckout, setDisableCheckout] = useState(false);
   const [buttonDisabled, setButtonDisabled] = useState({});
   const [productsLoaded, setProductsLoaded] = useState(false);

   const dispatch = useDispatch();

   const cartId = useMemo(() => getCartId(user, value?.shopName), [user?.carts, value?.shopName]);

   const isLoggedIn = () => {
      // Public user has authUser set as an ID not an object
      return !!(authUser && typeof authUser === "object" && Object.values(authUser).length);
   };

   useEffect(() => {
      checkWarnings();
      if (value?.shopName && value?.listId && value?.orderWorkflowConfigId) {
         if (isLoggedIn()) {
            getUserWithBoards()
               .then(() => {
                  if (!cartId) {
                     setProductsLoaded(true);
                     return;
                  }
                  const items = cart?.[value.shopName]?.addedItems;
                  const syncPromises = [];
                  const itemsWithStockErrors = new Set();
                  if (!cart?.[value.shopName]?.wasFetched && items?.length) {
                     items.forEach((item) => {
                        const quantityNotNull = item.quantity || 1;
                        // productId is set only for items fetched via basket API mapping.
                        // Items added directly from ProductItem only have item_id — use it as fallback.
                        const itemIdForCart = item.productId ?? item.item_id;
                        const p = postCart(
                           value.shopName,
                           cartId,
                           itemIdForCart,
                           quantityNotNull.toString(),
                           addProduct,
                        )
                           .then((res) => {
                              // HTTP-200 business error — track stock/budget issues for warning display
                              if (res?.data?.error && isBudgetError(res.data.error)) {
                                 itemsWithStockErrors.add(item.item_id);
                              }
                              return res;
                           })
                           .catch((error) => {
                              if (error?.response?.data?.message?.includes("Not enough budget")) {
                                 itemsWithStockErrors.add(item.item_id);
                              }
                           });
                        syncPromises.push(p);
                     });
                  }
                  // Wait for all pending postCart calls — each response contains full cartItems.
                  // Use the last successful response to merge backend cart with local product data.
                  Promise.allSettled(syncPromises).then((results) => {
                     // Only treat as authoritative when at least one postCart truly succeeded
                     // (returned a cart object). Errors are caught and return undefined, so
                     // filtering on r.value?.data?.cart distinguishes real success from swallowed errors.
                     const backendCart = results
                        .filter((r) => r.status === "fulfilled" && r.value?.data?.cart)
                        .map((r) => r.value.data.cart)
                        .pop();

                     if (backendCart) {
                        // At least one postCart succeeded — merge backend cart with local display data.
                        // Local items already contain title, price, photo etc. from when they were added.
                        const backendCartItems = backendCart.cartItems ?? [];
                        const localItems = cart?.[value.shopName]?.addedItems ?? [];
                        // Build a Map for O(1) lookup instead of O(n) find per iteration
                        const localByProductId = new Map(
                           localItems.flatMap((li) => {
                              const entries = [];
                              if (li.productId != null) entries.push([li.productId.toString(), li]);
                              if (li.item_id != null) entries.push([li.item_id.toString(), li]);
                              return entries;
                           }),
                        );
                        const mergedItems = backendCartItems.map((backendItem) => {
                           const key = backendItem.productId?.toString();
                           const localItem = key ? localByProductId.get(key) : undefined;
                           if (localItem) {
                              return {
                                 ...localItem,
                                 quantity: parseFloat(backendItem.quantity) || localItem.quantity,
                              };
                           }
                           // Backend-only item: no local display data available yet
                           return {
                              item_id: backendItem.productId,
                              productId: backendItem.productId,
                              quantity: parseFloat(backendItem.quantity) || 1,
                              unit: backendItem.unit,
                           };
                        });
                        dispatch(setCart(value.shopName, mergedItems));
                        dispatch(setHasBeenFetched(value.shopName));
                     } else if (syncPromises.length > 0) {
                        // All postCart calls failed — do NOT mark as fetched so sync
                        // can be retried on the next load. Keep local cart intact.
                     } else {
                        // No sync needed (wasFetched=true) — local cart already correct.
                        dispatch(setHasBeenFetched(value.shopName));
                     }
                     // Apply stock error warnings regardless of partial/full sync failure.
                     // Covers both: all-failed sync and mixed-result sync with some stock errors.
                     if (itemsWithStockErrors.size > 0) {
                        setWarnings((prev) => {
                           const next = { ...prev };
                           itemsWithStockErrors.forEach((id) => {
                              next[id] = true;
                           });
                           return next;
                        });
                        setDisableCheckout(true);
                     }
                     setProductsLoaded(true);
                  });
               })
               .catch(() => {
                  Message.error(intl.formatMessage({ id: "shopping.basket.somethingWrong" }));
                  setProductsLoaded(true);
               });
         } else {
            setProductsLoaded(true);
         }
      } else {
         setProductsLoaded(true);
      }
   }, [value, cartId]);
   useEffect(() => {
      if (listWidgetSeries && user && value?.listId && value?.shopName && cartId) {
         getFilteredList(value.listId, 1, 100, "SalesOrdersItems.id_orders", cartId, null)
            .then((res) => {
               const columnsCopy = [];
               const responseData = isActivePublicUser()
                  ? res.data.getPublicList
                  : res.data.default;
               if (responseData) {
                  responseData.columns.map((column) => {
                     if (
                        (column.options.visible == "1" || column.options.resolver) &&
                        column.options.techName
                     ) {
                        columnsCopy.push({
                           title: column?.label,
                           dataIndex: column?.label,
                           techName: column?.options?.techName,
                           precision: column?.options?.precision,
                        });
                     }
                     return null;
                  });
               }

               let rowsTable = responseData
                  ? responseData.data.map((row) => {
                       const obj = {};
                       columnsCopy.forEach((column) => {
                          if (typeof row[column.dataIndex] === "string") {
                             let rowValue = Number(row[column.dataIndex]);
                             // This has to check if rowValue is an empty string or not, because Number() makes converts empty string to 0
                             if (
                                row[column.dataIndex] &&
                                column.precision &&
                                row[column.dataIndex].length > 0 &&
                                !isNaN(rowValue)
                             ) {
                                obj[column.dataIndex] = rowValue
                                   .toFixed(column.precision)
                                   .toString();
                             } else {
                                obj[column.dataIndex] = row[column.dataIndex];
                             }
                          } else {
                             obj[column.dataIndex] = row[column.dataIndex];
                          }
                       });
                       return obj;
                    })
                  : [];

               rowsTable.forEach((row, i) => {
                  row.action = (
                     <ActionsDropdown
                        actions={responseData.actions}
                        title={"Actions"}
                        dataModelName={responseData.meta.model}
                        row={responseData.data[i]}
                        id={row?.api_rowMetaData?.recordId}
                     />
                  );
               });

               const actionsButtons = rowsTable.map((row, i) => {
                  if (responseData.actions.length > 0)
                     return (
                        <ActionsDropdown
                           actions={responseData.actions}
                           title={"Actions"}
                           dataModelName={responseData.meta.model}
                           row={responseData.data[i]}
                           id={row?.api_rowMetaData?.recordId}
                           isUserLoggedIn={isLoggedIn()}
                        />
                     );
                  else return null;
               });

               setButtons(actionsButtons);
               setColumns(columnsCopy);
               // Filtering items to only show items from your basket
               // TODO: THIS A TEMPORARY SOLUTION, NEEDS CHANGING LATER
               const query = new URLSearchParams(window.location.search);
               const basketId = query.get("basketId");
               if (basketId) {
                  const yourBasket = rowsTable.filter((item) => {
                     return item["ID zamówienia"] === basketId;
                  });
                  setRows(yourBasket);
               } else setRows(rowsTable);
            })
            .catch(() => {});
      }
   }, [listWidgetSeries, value?.listId, value?.shopName, cartId]);

   // Using debouncing only for onChange because it doesn't matter for the plus/minus buttons
   const debounced = useDebouncedCallback((itemId, productId, quantity) => {
      if (isLoggedIn())
         postCart(value.shopName, cartId, productId ?? itemId, quantity.toString(), setQuantity)
            .then((res) => {
               // API returns HTTP 200 even on errors — check body for error field.
               // Only block on error when increasing — decreasing should always be allowed.
               if (res?.data?.error) {
                  const currentQuantity = getQuantity(findItemIndex(itemId));
                  if (quantity > currentQuantity) {
                     if (isBudgetError(res.data.error)) {
                        // Revert the optimistic display — the pre-attempt quantity was valid.
                        // Use functional setWarnings to read fresh state, avoiding stale closure.
                        setValues((prev) => ({ ...prev, [itemId]: currentQuantity }));
                        setWarnings((prev) => {
                           const next = { ...prev, [itemId]: false };
                           setDisableCheckout(
                              !value?.shopName ||
                                 !value?.orderWorkflowConfigId ||
                                 !value?.listId ||
                                 Object.values(next).some((v) => v === true),
                           );
                           return next;
                        });
                     }
                     Message.error(intl.formatMessage({ id: res.data.error }));
                     return;
                  }
               }
               disableButtonHandler(itemId, increase, false);
               checkWarnings();
               dispatch(changeProductQuantity(value.shopName, itemId, quantity));
            })
            .catch((error) => {
               errorHandler(itemId, error?.response?.data?.message?.includes("Not enough budget"));
            });
      else {
         disableButtonHandler(itemId, increase, false);
         dispatch(changeProductQuantity(value.shopName, itemId, quantity));
      }
   }, 1000);

   const setCategories = (itemId) => {
      const index = findItemIndex(itemId);
      if (
         cart?.[value.shopName]?.addedItems[index]?.categories?.length &&
         value?.showAdditionalInfo
      ) {
         return cart?.[value.shopName].addedItems[index].categories.map((category) => {
            return (
               <div>
                  <p key={category}>{category}</p>
               </div>
            );
         });
      }
      return null;
   };

   const findItemIndex = (itemId) => {
      if (cart?.[value.shopName]?.addedItems)
         return cart?.[value.shopName].addedItems.findIndex((item) => item.item_id === itemId);
   };

   const getQuantity = (index) => {
      if (cart?.[value.shopName]?.addedItems) {
         const quantity = cart?.[value.shopName].addedItems[index].quantity;
         let parsedQuantity;
         if (typeof quantity === "string") parsedQuantity = parseInt(quantity);
         else parsedQuantity = quantity;
         return parsedQuantity;
      }
   };

   const getPrice = (index) => {
      if (cart?.[value.shopName]?.addedItems) {
         const price = cart?.[value.shopName].addedItems[index]?.unit_price;
         let parsedPrice;
         // toFixed(2) seems like a reasonable value for prices
         if (typeof price === "string") parsedPrice = parseFloat(price).toFixed(2);
         else parsedPrice = price;
         return parsedPrice;
      }
   };

   const getCurrentPrice = (itemId) => {
      // TODO: Might need to add backend check in future in case of promotions
      if (cart?.[value.shopName]?.addedItems) {
         const index = findItemIndex(itemId);
         const quantity = getQuantity(index);
         const price = getPrice(index);
         const currentPrice = quantity * price;
         return Number(currentPrice.toFixed(2));
      }
   };

   const getTotalPrice = () => {
      // TODO: Might need to add backend check in future in case of promotions
      if (cart?.[value.shopName]?.addedItems?.length) {
         const items = Object.values(cart?.[value.shopName].addedItems);
         const prices = items.map((item) => getCurrentPrice(item.item_id) || 0);
         const totalPrice = prices.reduce((prev, next) => prev + next, 0);
         if (!isNaN(Number(totalPrice))) {
            return Number(totalPrice).toFixed(2);
         }
      }
   };

   const allowExceed = () => {
      return !!value?.allowExceedStoreLimit;
   };

   const disableButtonHandler = (itemId, type, boolean) => {
      setButtonDisabled({
         ...buttonDisabled,
         [itemId]: {
            ...buttonDisabled[itemId],
            [type]: boolean,
         },
      });
   };

   const checkWarnings = () => {
      if (!value?.shopName || !value?.orderWorkflowConfigId || !value?.listId)
         setDisableCheckout(true);
      else {
         const warningValues = Object.values(warnings);
         setDisableCheckout(warningValues.length > 0 && warningValues.some((v) => v === true));
      }
   };

   const errorHandler = (itemId, errorBudgetExceededMsg) => {
      setDisableCheckout(true);
      const index = findItemIndex(itemId);
      if (index > -1) {
         const quantity = cart?.[value.shopName]?.addedItems[index].quantity;
         setValues({ ...values, [itemId]: quantity });
      }
      if (!allowExceed() && errorBudgetExceededMsg) {
         setWarnings({ ...warnings, [itemId]: true });
         disableButtonHandler(itemId, increase, true);
      }
      // return message.error(intl.formatMessage({ id: "shopping.basket.somethingWrong" }));
   };

   const setImageProperties = (property) => {
      if (typeof property === "number") return property.toString();
      return property;
   };

   const quantityButtons = (type, itemId, productId) => {
      const itemIndex = findItemIndex(itemId);
      if (itemIndex > -1) {
         const quantity = getQuantity(itemIndex);
         let changedQuantity;
         // Determine whether to increase or decrease
         if (type === increase) changedQuantity = quantity + 1;
         else changedQuantity = quantity - 1;
         // Don't allow input to go below 0 - reset disabled button onChange
         if (changedQuantity <= 0) disableButtonHandler(itemId, decrease, true);
         else disableButtonHandler(itemId, decrease, false);
         // Reset warnings onChange and set local value
         setWarnings({ ...warnings, [itemId]: false });
         setValues({ ...values, [itemId]: changedQuantity });
         // Dispatch to redux or handle error
         if (isLoggedIn())
            postCart(
               value.shopName,
               cartId,
               productId ?? itemId,
               changedQuantity.toString(),
               setQuantity,
            )
               .then((res) => {
                  // API returns HTTP 200 even on errors — check body for error field.
                  // Only block on error when increasing — decreasing should always be allowed.
                  if (res?.data?.error && type === increase) {
                     if (isBudgetError(res.data.error)) {
                        // Revert the optimistic display — the pre-attempt quantity was valid.
                        // Use functional setWarnings to read fresh state, avoiding stale closure.
                        setValues((prev) => ({ ...prev, [itemId]: quantity }));
                        setWarnings((prev) => {
                           const next = { ...prev, [itemId]: false };
                           setDisableCheckout(
                              !value?.shopName ||
                                 !value?.orderWorkflowConfigId ||
                                 !value?.listId ||
                                 Object.values(next).some((v) => v === true),
                           );
                           return next;
                        });
                     }
                     Message.error(intl.formatMessage({ id: res.data.error }));
                     return;
                  }
                  setWarnings({ ...warnings, [itemId]: false });
                  checkWarnings();
                  dispatch(changeProductQuantity(value.shopName, itemId, changedQuantity));
               })
               .catch((error) => {
                  errorHandler(
                     itemId,
                     error?.response?.data?.message?.includes("Not enough budget"),
                  );
               });
         else dispatch(changeProductQuantity(value.shopName, itemId, changedQuantity));
      }
   };

   const handleQuantityChange = (e, itemId, productId) => {
      // Don't allow input to go to below 0
      if (e.target.value <= 1) {
         e.target.value = 1;
         disableButtonHandler(itemId, decrease, true);
      } else disableButtonHandler(itemId, decrease, false);
      // Only dispatch to Redux if request succeeds, else set value back to last correct one (the one stored in Redux)
      const parsedQuantity = parseInt(e.target.value, 10);
      setWarnings({ ...warnings, [itemId]: false });
      setValues({ ...values, [itemId]: parsedQuantity });
      debounced(itemId, productId, parsedQuantity);
   };

   const removeItem = (itemId, productId) => {
      const itemIndex = findItemIndex(itemId);
      if (itemIndex > -1) {
         const quantity = getQuantity(itemIndex);
         const deleteItem = () => {
            dispatch(removeCartItem(value.shopName, itemId));
            setValues((prev) => {
               const next = { ...prev };
               delete next[itemId];
               return next;
            });
            setWarnings((prev) => {
               const next = { ...prev };
               delete next[itemId];
               return next;
            });
            setButtonDisabled((prev) => {
               const next = { ...prev };
               delete next[itemId];
               return next;
            });
         };
         if (isLoggedIn())
            postCart(
               value.shopName,
               cartId,
               productId ?? itemId,
               quantity.toString(),
               deleteProduct,
            )
               .then((res) => {
                  if (res?.data?.error) {
                     Message.error(intl.formatMessage({ id: res.data.error }));
                     return;
                  }
                  deleteItem();
               })
               .catch(() => {
                  Message.error(intl.formatMessage({ id: "shopping.basket.somethingWrong" }));
               });
         else {
            deleteItem();
         }
      }
   };

   const emptyCart = () => {
      // Not using clearCart here because I need to remove the items from backend, not just from Redux
      if (cart?.[value.shopName]?.addedItems) {
         const items = Object.values(cart?.[value.shopName].addedItems);
         const ids = items.map((item) => item.item_id);
         ids.forEach((id) => removeItem(id, items.find((item) => item.item_id === id)?.productId));
      }
   };

   const onCheckout = () => {
      if (projectConfig?.shop?.points && userPoints < Number(getTotalPrice())) {
         Message.error(intl.formatMessage({ id: "basket.tooFewPoints" }));
         return;
      }
      if (cart?.[value?.shopName]?.addedItems?.length > 0) {
         // Redirect to login/register on checkout if user is not logged in
         if (!isLoggedIn()) {
            if (isCzg() && window.location.pathname !== "/") {
               sessionStorage.setItem("redirectCzgLink", window.location.pathname);
            }
            mobileStartActionByUrl(`/signin`);
            mobileStartFlutterActionByUrl("startActionByUrl", `/signin`);
            return history.push(`/signin`);
         }
         // Might need to use runProcess (function from shop with the same name)
         if (!cartId) {
            Message.error(intl.formatMessage({ id: "shopping.basket.somethingWrong" }));
            return;
         }
         const recordId = value.orderWorkflowConfigId;
         const dataModelName = "SalesOrders";
         const url = `/cases/${recordId}?processId=${recordId}&dataModelName=${dataModelName}&dataId=${cartId}`;
         mobileStartActionByUrl(url);
         mobileStartFlutterActionByUrl("startActionByUrl", url);
         history.push(url);
      } else {
         Message.error(intl.formatMessage({ id: "shopping.basket.emptyBasket" }));
      }
   };

   const showSpinner = () => {
      if (cart[value?.shopName]?.addedItems?.length) return <Spin />;
      return null;
   };

   const getPricePerUnit = (perUnitPrice) => {
      if (!isNaN(Number(perUnitPrice))) {
         return Number(perUnitPrice).toFixed(2);
      }
   };

   useEffect(() => {
      if (projectConfig?.shop?.points?.listId) {
         getList(projectConfig.shop.points.listId, 1, 1000).then((res) => {
            const responseData = isActivePublicUser() ? res.data.getPublicList : res.data.default;
            const userInfo = responseData.data.find(
               (dataPoint) => dataPoint["ID Użytkownika"] === user.oldId?.toString(),
            );
            const pointsValue = userInfo
               ? Number(userInfo[projectConfig.shop.points.columnLabel])
               : NaN;
            setUserPoints(isNaN(pointsValue) ? null : pointsValue.toFixed(0));
         });
      }
   }, []);

   return (
      <Widget
         className="Widget"
         role={role}
         extra={
            role === "OWNER" || role === "MODERATOR" ? (
               <SettingsModal
                  widgetName={value.name}
                  widgetId={value.id}
                  icon="setting"
                  deleteIcon="true"
                  children={<WidgetShoppingBasket value={value} role={role} columns={columns} />}
               />
            ) : null
         }
      >
         <div>
            <div>
               {value && productsLoaded && cart?.[value?.shopName]?.addedItems?.length
                  ? cart[value.shopName].addedItems.map((item, index) => {
                       const photo = item?.photoColumn ? "photoColumn" : "Zdjęcie";
                       return (
                          <Card key={item.item_id} className="product-card">
                             <div className="product-basket-component">
                                {value.showImage && item[photo] && (
                                   <img
                                      className="product-image"
                                      alt={item.title}
                                      src={
                                         item[photo][0]?.altUrl ??
                                         item[photo][0]?.altUrl1 ??
                                         item[photo][0]?.fileUrl
                                      }
                                      width={`${setImageProperties(value?.imageWidth)}px`}
                                      height={`${setImageProperties(value?.imageHeight)}px`}
                                   />
                                )}
                                <div className="product-info">
                                   <div className="product-key-info">
                                      <h2>{item.title}</h2>

                                      <KeyInfoItem
                                         showTitle={value.showKeyInfo}
                                         value={
                                            (value.keyInfo !== "-" || value.keyInfo === null) &&
                                            fixedHTML(rows[value.keyInfo])
                                         }
                                         title={value.keyInfo}
                                      />

                                      <div className="categories">
                                         {setCategories(item.item_id)}
                                      </div>
                                   </div>

                                   {isLoggedIn() && value.showPrice ? (
                                      <>
                                         <div className="price-values">
                                            <div className="price-container">
                                               <p>
                                                  <IntlMessages id="shopping.basket.price" />
                                               </p>
                                               {getCurrentPrice(item.item_id) ? (
                                                  <p>
                                                     <strong>
                                                        {getCurrentPrice(item.item_id)}{" "}
                                                        {value.currency ? value.currency : null}
                                                     </strong>
                                                  </p>
                                               ) : null}
                                            </div>
                                            <div className="price-container">
                                               <p>
                                                  <IntlMessages id="shopping.basket.perUnit" />
                                               </p>
                                               <p>
                                                  <strong>
                                                     {getPricePerUnit(item.unit_price)}{" "}
                                                     {value.currency ? value.currency : null}
                                                  </strong>
                                               </p>
                                            </div>
                                         </div>
                                      </>
                                   ) : null}
                                   {item.orderType?.value ? <h4>{item.orderType.value}</h4> : ""}

                                   {value.changeQuantity ? (
                                      <div className="product-quantity-container">
                                         <Button
                                            buttonType="primary"
                                            className="basket-btn"
                                            onClick={() =>
                                               quantityButtons(
                                                  decrease,
                                                  item.item_id,
                                                  item.productId,
                                               )
                                            }
                                            disabled={
                                               buttonDisabled?.[item.item_id]?.decrease ||
                                               item.quantity <= 1
                                                  ? true
                                                  : false
                                            }
                                         >
                                            <Icon_Minus />
                                         </Button>
                                         <Input
                                            className="basket-input"
                                            type="number"
                                            min="1"
                                            onChange={(e) =>
                                               handleQuantityChange(e, item.item_id, item.productId)
                                            }
                                            value={
                                               values?.[item.item_id]
                                                  ? values[item.item_id]
                                                  : item.quantity
                                            }
                                         />

                                         <Button
                                            buttonType="primary"
                                            className="basket-btn"
                                            onClick={() =>
                                               quantityButtons(
                                                  increase,
                                                  item.item_id,
                                                  item.productId,
                                               )
                                            }
                                            disabled={
                                               buttonDisabled?.[item.item_id]?.increase
                                                  ? true
                                                  : false
                                            }
                                         >
                                            <Icon_Plus />
                                         </Button>
                                      </div>
                                   ) : null}
                                </div>
                                <div className="product-container-for-buttons">
                                   <div className="delete-button-container">
                                      {/* trash bin delete */}
                                      <Button
                                         onClick={() => removeItem(item.item_id, item.productId)}
                                         buttonType="danger"
                                         className="basket-btn"
                                      >
                                         <Icon_Delete />
                                      </Button>
                                   </div>
                                   {value?.showActions && (
                                      <div className="action-buttons-container">
                                         <div style={{ maxWidth: "200px" }}>{buttons[index]}</div>
                                      </div>
                                   )}
                                </div>
                             </div>
                             {warnings?.[item.item_id] ? (
                                <Card
                                   title={
                                      warnings?.[item.item_id] ? (
                                         <p className="warning-text">
                                            <IntlMessages id="shopping.basket.exceeded" />
                                         </p>
                                      ) : null
                                   }
                                ></Card>
                             ) : null}
                          </Card>
                       );
                    })
                  : showSpinner()}
            </div>
            <div className="summary-column">
               <Card className="summary-card">
                  {projectConfig?.shop?.points && !isActivePublicUser() && (
                     <div className="summary-card__points">
                        {typeof userPoints === "undefined" ? (
                           <Icon_Loading />
                        ) : (
                           projectConfig.shop.points.name + ": " + userPoints
                        )}
                     </div>
                  )}
                  <div className="summary-title">
                     <h2>
                        <IntlMessages id="shopping.basket.summary" />
                     </h2>
                  </div>
                  {isLoggedIn() && getTotalPrice() && value?.showPrice && productsLoaded ? (
                     <div className="total-value-container">
                        <h3>
                           <IntlMessages id="shopping.basket.total.value" />
                        </h3>
                        <h3>
                           {getTotalPrice()} {value?.currency}
                        </h3>
                     </div>
                  ) : null}
                  <div className="action-buttons-container">
                     <Button
                        className="basket-btn"
                        onClick={onCheckout}
                        buttonType="primary"
                        disabled={disableCheckout}
                     >
                        <IntlMessages id="shopping.basket.checkout" />
                     </Button>

                     {cart?.[value?.shopName]?.addedItems?.length ? (
                        <Button className="basket-btn" onClick={emptyCart} buttonType="danger">
                           <IntlMessages id="shopping.basket.clear" />
                        </Button>
                     ) : null}
                  </div>
               </Card>
            </div>
         </div>
      </Widget>
   );
};
export default ShoppingBasketCardsWidget;
